import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accountsApi, Account } from '../api/accounts';
import { leadsApi } from '../api/leads';
import { contactsApi } from '../api/contacts';
import { tasksApi } from '../api/tasks';
import { activitiesApi } from '../api/activities';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import Tooltip from '../components/Tooltip';
import { formatCurrency } from '../utils/currencyUtils';
import { parseLeadValue, parseLeadProbability } from '../utils/leadUtils';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PhoneIcon,
  GlobeAltIcon,
  MapPinIcon,
  ChartBarIcon,
  CurrencyRupeeIcon,
  PlusIcon,
  PencilIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// Stage definitions matching Leads.tsx
const stages = [
  { id: 'new', name: 'New', color: 'bg-slate-500' },
  { id: 'contacted', name: 'Contacted', color: 'bg-blue-500' },
  { id: 'first_meeting', name: 'First Meeting', color: 'bg-cyan-500' },
  { id: 'second_meeting', name: 'Second Meeting', color: 'bg-teal-500' },
  { id: 'qualified', name: 'Qualified', color: 'bg-emerald-500' },
  { id: 'pitch_presented', name: 'Pitch Presented', color: 'bg-amber-500' },
  { id: 'sow_in_progress', name: 'SOW in Progress', color: 'bg-orange-500' },
  { id: 'sow_sent', name: 'SOW Sent', color: 'bg-pink-500' },
  { id: 'clarification_call', name: 'Clarification', color: 'bg-fuchsia-500' },
  { id: 'roi_proof_call', name: 'ROI/Proof', color: 'bg-purple-500' },
  { id: 'decision_stage', name: 'Decision', color: 'bg-violet-500' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-indigo-500' },
  { id: 'won', name: 'Won', color: 'bg-green-500' },
  { id: 'lost', name: 'Lost', color: 'bg-red-500' },
];

const AccountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'leads' | 'contacts' | 'activities' | 'tasks'>('leads');

  const accountId = parseInt(id || '0');

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountsApi.getById(accountId),
    enabled: !!accountId,
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.getAll,
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['tasks'],
    queryFn: tasksApi.getAll,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['activities'],
    queryFn: activitiesApi.getAll,
  });

  // Filter data for this account
  const accountLeads = leads.filter(l => l.account_id === accountId);
  const accountContacts = contacts.filter(c => c.account_id === accountId);
  const accountTasks = tasks.filter(t => t.account_id === accountId);
  const accountActivities = activities.filter(a => a.account_id === accountId);

  // Calculate metrics
  const totalPipelineValue = accountLeads.filter(l => !['won', 'lost'].includes(l.stage)).reduce((sum, l) => 
    sum + parseLeadValue(l.value), 0);
  const wonValue = accountLeads.filter(l => l.stage === 'won').reduce((sum, l) => 
    sum + parseLeadValue(l.value), 0);
  const wonLeads = accountLeads.filter(l => l.stage === 'won').length;
  const activeLeads = accountLeads.filter(l => !['won', 'lost'].includes(l.stage)).length;

  if (accountLoading) {
    return (
      <PremiumLayout title="Account Details">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (!account) {
    return (
      <PremiumLayout title="Account Not Found">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Account not found</h2>
          <p className="text-gray-500 mb-4">The account you're looking for doesn't exist or you don't have access.</p>
          <Link to="/accounts" className="btn-primary">
            Back to Accounts
          </Link>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title=""
      actions={
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/accounts')}
            className="btn-secondary text-sm px-4 py-2"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Accounts
          </button>
          <button className="btn-primary text-sm px-4 py-2">
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit Account
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <BuildingOfficeIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
                {account.industry && (
                  <span className="inline-flex items-center mt-1 text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    {account.industry}
                  </span>
                )}
                <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                  {account.website && (
                    <a href={account.website} target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-indigo-600">
                      <GlobeAltIcon className="h-4 w-4 mr-1" />
                      {account.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                  {account.phone && (
                    <a href={`tel:${account.phone}`} className="flex items-center hover:text-indigo-600">
                      <PhoneIcon className="h-4 w-4 mr-1" />
                      {account.phone}
                    </a>
                  )}
                </div>
                {account.address && (
                  <div className="flex items-start mt-2 text-sm text-gray-500">
                    <MapPinIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{account.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tooltip content="Total number of leads for this account" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{accountLeads.length}</div>
                  <div className="text-xs text-gray-500">Total Leads</div>
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip content="Value of leads currently in pipeline" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CurrencyRupeeIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{formatCurrency(totalPipelineValue, 'INR', { compact: true })}</div>
                  <div className="text-xs text-gray-500">Pipeline Value</div>
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip content="Deals won from this account" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{wonLeads}</div>
                  <div className="text-xs text-gray-500">Won Deals</div>
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip content="Number of contacts at this company" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <UserGroupIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{accountContacts.length}</div>
                  <div className="text-xs text-gray-500">Contacts</div>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'leads', label: `Leads (${accountLeads.length})`, icon: ChartBarIcon },
                { id: 'contacts', label: `Contacts (${accountContacts.length})`, icon: UserGroupIcon },
                { id: 'activities', label: `Activities (${accountActivities.length})`, icon: InformationCircleIcon },
                { id: 'tasks', label: `Tasks (${accountTasks.length})`, icon: CheckCircleIcon },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'leads' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">All Leads from {account.name}</h3>
                  <Link to="/leads" className="btn-secondary text-sm">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Lead
                  </Link>
                </div>
                {accountLeads.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stage</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Value</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {accountLeads.map((lead) => {
                          const stageInfo = stages.find(s => s.id === lead.stage) || stages[0];
                          return (
                            <tr 
                              key={lead.id} 
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => navigate(`/leads/${lead.id}`)}
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{lead.brand || lead.name}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {lead.lead_name || '—'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${stageInfo.color} text-white`}>
                                  {stageInfo.name}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {lead.category ? (
                                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded font-bold text-xs text-white ${
                                    lead.category === 'A' ? 'bg-emerald-500' : 
                                    lead.category === 'B' ? 'bg-amber-500' : 'bg-gray-500'
                                  }`}>
                                    {lead.category}
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {formatCurrency(parseLeadValue(lead.value), 'INR', { compact: true })}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No leads for this account yet. <Link to="/leads" className="text-indigo-600 hover:underline">Create one</Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'contacts' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Contacts at {account.name}</h3>
                  <Link to="/contacts" className="btn-secondary text-sm">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Contact
                  </Link>
                </div>
                {accountContacts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {accountContacts.map((contact) => (
                      <div 
                        key={contact.id} 
                        className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => navigate(`/contacts?search=${contact.first_name}`)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {contact.first_name.charAt(0)}{contact.last_name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{contact.first_name} {contact.last_name}</div>
                            {contact.title && <div className="text-xs text-gray-500">{contact.title}</div>}
                            <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                              {contact.email && (
                                <a href={`mailto:${contact.email}`} className="hover:text-indigo-600" onClick={(e) => e.stopPropagation()}>
                                  {contact.email}
                                </a>
                              )}
                              {contact.phone && (
                                <a href={`tel:${contact.phone}`} className="hover:text-indigo-600" onClick={(e) => e.stopPropagation()}>
                                  {contact.phone}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No contacts for this account yet. <Link to="/contacts" className="text-indigo-600 hover:underline">Add one</Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activities' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Activity History</h3>
                  <Link to="/activities" className="btn-secondary text-sm">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Log Activity
                  </Link>
                </div>
                {accountActivities.length > 0 ? (
                  <div className="space-y-4">
                    {accountActivities.map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          activity.type === 'call' ? 'bg-blue-100' :
                          activity.type === 'email' ? 'bg-green-100' :
                          activity.type === 'meeting' ? 'bg-purple-100' : 'bg-gray-100'
                        }`}>
                          <span className="text-lg">
                            {activity.type === 'call' ? '📞' :
                             activity.type === 'email' ? '✉️' :
                             activity.type === 'meeting' ? '📅' : '📝'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">{activity.subject}</h4>
                            <span className="text-xs text-gray-500">
                              {new Date(activity.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No activities yet. <Link to="/activities" className="text-indigo-600 hover:underline">Log one</Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Tasks</h3>
                  <Link to="/tasks" className="btn-secondary text-sm">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Task
                  </Link>
                </div>
                {accountTasks.length > 0 ? (
                  <div className="space-y-3">
                    {accountTasks.map((task) => (
                      <div key={task.id} className={`flex items-center justify-between p-4 rounded-lg border ${
                        task.status === 'completed' ? 'bg-green-50 border-green-200' :
                        task.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <CheckCircleIcon className={`h-5 w-5 ${
                            task.status === 'completed' ? 'text-green-500' :
                            task.status === 'in_progress' ? 'text-blue-500' : 'text-gray-400'
                          }`} />
                          <div>
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            {task.due_date && (
                              <p className="text-xs text-gray-500">
                                Due: {new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No tasks yet. <Link to="/tasks" className="text-indigo-600 hover:underline">Create one</Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PremiumLayout>
  );
};

export default AccountDetail;
